import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// =============================================================================
// Query Keys
// =============================================================================

export const KEYS = {
    stats: ['stats'],
    identities: (limit: number, offset: number, search: string) => [
        'identities',
        limit,
        offset,
        search,
    ],
    logs: (
        limit: number,
        offset: number,
        level: string,
        source: string,
        requestId: string,
        userId: string
    ) => ['logs', limit, offset, level, source, requestId, userId],
} as const;

// =============================================================================
// Hooks
// =============================================================================

export function useStats() {
    return useQuery({
        queryKey: KEYS.stats,
        queryFn: () => api.admin.stats(),
        select: (result) => result.data,
    });
}

export interface Identity {
    username: string;
    state: string;
    created_at: string;
    expires_at: string | null;
}

export function useIdentities(page: number, limit: number, search: string) {
    const offset = page * limit;
    return useQuery({
        queryKey: KEYS.identities(limit, offset, search),
        queryFn: () => api.admin.listIdentities(limit, offset, search),
        select: (result) => {
            const data = result.data;
            // Normalize response (handle both array and object formats)
            if (Array.isArray(data)) {
                return { identities: data as Identity[], total: data.length };
            }
            // Assume object format { identities, total }
            // The API client type says 'untyped' but we know the structure
            const response = data as { identities: Identity[]; total: number };
            if (response && Array.isArray(response.identities)) {
                return response;
            }
            return { identities: [], total: 0 };
        },
        placeholderData: keepPreviousData,
    });
}

export interface AdminLog {
    id: string;
    level: string;
    message: string;
    source: string;
    timestamp: string;
    requestId?: string;
    userId?: string;
    metadata?: string;
}

export function useLogs(
    page: number,
    limit: number,
    level: string,
    source: string,
    requestId: string,
    userId: string
) {
    const offset = page * limit;
    return useQuery({
        queryKey: KEYS.logs(limit, offset, level, source, requestId, userId),
        queryFn: () => api.admin.logs(limit, offset, level, source, requestId, userId),
        select: (result) => {
            const data = result.data;
            // Normalize response
            if (Array.isArray(data)) {
                return { logs: data as AdminLog[], total: data.length };
            }
            const response = data as { logs: AdminLog[]; total: number };
            if (response && Array.isArray(response.logs)) {
                return response;
            }
            return { logs: [], total: 0 };
        },
        placeholderData: keepPreviousData,
    });
}

// =============================================================================
// Mutations
// =============================================================================

export function useActivateIdentity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (username: string) => api.admin.activateIdentity(username),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['identities'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
        },
    });
}

export function useRevokeIdentity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (username: string) => api.admin.revokeIdentity(username),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['identities'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
        },
    });
}
